// StatBlock — a labeled value. The host provides values for built-in
// `kind`s; unknown kinds defer to sceneFeatures.
//
// "price" needs a real price source — not wired in v1 (step 8 lands it
// alongside the measurements feature). "count" reflects placed instances.

import {
  resolveContentSource,
  type ContentSource,
  type ContentSourceContext,
} from "./contentSource";
import { formatDecimal } from "../measurement";
import type { BlockHostContext, BlockUniversalProps } from "./types";

export interface StatBlockProps extends BlockUniversalProps {
  kind: "price" | "count" | "measurement" | string;
  /** Literal label for v1; step 9 swaps to a DVar id. */
  label?: string;
  labelDvarId?: number;
  host: BlockHostContext;
  /** Content source for the label (literal / dvar / from-*). */
  labelSource?: ContentSource;
  /** Unit noun appended to the value — translatable.
   *  - kind=count: singular / plural forms picked by the count ("1 Element" /
   *    "3 Elements"). Empty → just the number.
   *  - kind=measurement: unitSingular is the single unit suffix after the
   *    "W × H" dimensions (e.g. "cm (w × h)"); unitPlural is unused. */
  unitSingular?: string;
  unitSingularSource?: ContentSource;
  unitPlural?: string;
  unitPluralSource?: ContentSource;
  /** Live content-source context (series + selection), handed down by Slot. */
  sourceCtx?: ContentSourceContext;
}

export function StatBlock({
  kind, label, themeVariant, className, host, labelSource,
  unitSingular, unitSingularSource, unitPlural, unitPluralSource, sourceCtx,
}: StatBlockProps) {
  const ctx = sourceCtx ?? {};
  const unitSingularResolved = resolveContentSource(unitSingularSource, unitSingular, ctx);
  const value = resolveValue(kind, host, {
    singular: unitSingularResolved,
    plural: resolveContentSource(unitPluralSource, unitPlural, ctx),
  });
  const resolvedLabel = resolveContentSource(labelSource, label, sourceCtx ?? {});
  // Render nothing when there's no value yet — keeps the layout clean during
  // the loading window for "price" before its data source lands.
  if (value == null) return null;
  // Measurement: render the unit suffix (e.g. "cm (w × h)") as a small,
  // de-emphasised span after the dimensions — matching how the old hardcoded
  // totals showed it (small <em>), rather than at the same size as the number.
  const measurementValue =
    kind === "measurement" && unitSingularResolved
      ? { dims: value.slice(0, value.length - unitSingularResolved.length).trimEnd(), unit: unitSingularResolved }
      : null;
  return (
    <div
      className={"sd-block-stat" + (className ? " " + className : "")}
      data-kind={kind}
      data-variant={themeVariant}
    >
      <span className="sd-block-stat__value">
        {measurementValue ? (
          <>
            {measurementValue.dims}{" "}
            <span className="sd-block-stat__unit">{measurementValue.unit}</span>
          </>
        ) : (
          value
        )}
      </span>
      {resolvedLabel && <span className="sd-block-stat__label">{resolvedLabel}</span>}
    </div>
  );
}

function resolveValue(
  kind: string,
  host: BlockHostContext,
  unit: { singular: string | null; plural: string | null },
): string | null {
  if (kind === "count") {
    const n = host.instanceCount;
    // Pick the operator-authored singular/plural unit noun (translatable). No
    // unit set → render just the number. The unit is no longer hardcoded so it
    // can be translated per language.
    const word = n === 1 ? unit.singular : unit.plural;
    return word ? `${n} ${word}` : `${n}`;
  }
  if (kind === "measurement") {
    // Scene dimensions (metres) from the host, formatted as "W × H <unit>" in
    // cm. Null when the scene is empty / dimensions unknown → renders nothing.
    // unit.singular is the translatable unit suffix (e.g. "cm (w × h)").
    const d = host.getFeature("sceneDimensions");
    if (!d || typeof d !== "object" || !("width" in d) || !("height" in d)) return null;
    const dims = d as { width: number; height: number };
    // Decimal separator resolved server-side (Designer setting + active
    // language); "." unless the host says otherwise.
    const sep = host.getFeature("decimalSeparator");
    const separator = sep === "," ? "," : ".";
    const w = formatDecimal(dims.width * 100, 1, separator);
    const h = formatDecimal(dims.height * 100, 1, separator);
    return unit.singular ? `${w} × ${h} ${unit.singular}` : `${w} × ${h}`;
  }
  if (kind === "price") {
    // Live total from the host's price source. `formatted` carries the
    // pricelist currency's display rules resolved server-side; the raw
    // amount + code is the fallback for sources that don't format.
    const f = host.getFeature("totalPrice");
    if (f && typeof f === "object" && "amount" in f && "currency" in f) {
      const v = f as { amount: number; currency: string; formatted?: string };
      return v.formatted ?? `${v.amount.toLocaleString()} ${v.currency}`;
    }
    return null;
  }
  // Generic fallback: any sceneFeature key whose value is string/number.
  const f = host.getFeature(kind);
  if (f == null) return null;
  if (typeof f === "string" || typeof f === "number") return String(f);
  return null;
}
